import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Form, Input, Modal, Typography, notification } from "antd";
import {
  CNF_PREPARE_BILLING_DISBURSE_ENDPOINT,
  CNF_PREPARE_BILLING_LIST_ENDPOINT,
  CNF_PREPARE_BILLING_REQUESTED_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EXTERNAL_CNF_BILLS_CREATE_PERMISSION,
  EXTERNAL_CNF_BILLS_EDIT_PERMISSION,
  EXTERNAL_CNF_BILLS_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import { checkPermission } from "./config/checkPermission";
import { ROLES } from "./config/constants/roles";
import PrepareBillingStatusTag from "./PrepareBillingStatusTag";
import { formatAmountTaka } from "./prepareBillingFormatters";

const { Title } = Typography;
const basePath = "/import-export-expense-tracking/external-cnf-bills/prepare-billing";

export default function PrepareBillingList() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);
  const loggedInUserRole = localStorage.getItem("user_type");

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [disburseModalVisible, setDisburseModalVisible] = useState(false);
  const [disburseRecord, setDisburseRecord] = useState(null);
  const [disburseForm] = Form.useForm();

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsed = filterStatus ? JSON.parse(filterStatus) : {};
    localStorage.setItem(
      "filter_status",
      JSON.stringify({
        ...parsed,
        [routerPath]: showFilter,
      }),
    );
  }, [showFilter, routerPath]);

  const fetchList = useCallback(
    async (filterValues) => {
      setLoading(true);
      setFetchError(null);
      const query = `${CNF_PREPARE_BILLING_LIST_ENDPOINT}?page=${currentPage}&per_page=${pageSize}`;
      const bodyData = {
        ...(filterValues?.name && { bill_number: filterValues.name }),
        ...(filterValues?.status && { status: filterValues.status }),
      };
      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        setTableData(
          data.map((row) => ({
            ...row,
            key: row.id,
          })),
        );
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultPageSize: response.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage, pageSize],
  );

  useEffect(() => {
    fetchList(filterData);
  }, [refresh, filterData, fetchList]);

  const refetch = useCallback(() => setRefresh((p) => p + 1), []);

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    const allSameStatus =
      newSelectedRows.length > 0 &&
      newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (allSameStatus) {
      setCanRequest(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "request",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
      setCanApprove(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "approve",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
      setCanReject(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "reject",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
    } else {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedRows.length) {
      notification.warning({
        message: "Warning",
        description: "No rows selected",
        placement: "topRight",
      });
      return;
    }

    const allSameStatus =
      selectedRows.length > 0 &&
      selectedRows.every((row) => row.status === selectedRows[0].status);
    const currentStatus = selectedRows[0]?.status;
    const isAllowedStatus = currentStatus === "created" || currentStatus === "rejected";

    if (!allSameStatus || !isAllowedStatus) {
      notification.warning({
        message: "Warning",
        description: "Only records with Created or Rejected status can be sent for approval.",
        placement: "topRight",
      });
      return;
    }

    const onlyOwnRecords = selectedRows.every(
      (row) => loggedInUserId === row?.user?.id,
    );
    if (!onlyOwnRecords) {
      notification.warning({
        message: "Warning",
        description: "You can only send your own prepare billing records for approval.",
        placement: "topRight",
      });
      return;
    }

    Modal.confirm({
      title: currentStatus === "created" ? "Confirm request" : "Confirm re-submission",
      content:
        currentStatus === "created"
          ? "Send the selected prepare billing to Head of Commercial & Shipping?"
          : "Re-submit the selected rejected prepare billing for approval?",
      okText: "Yes",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append("requested_id", JSON.stringify(selectedRows.map((r) => r.id)));
        formData.append("status", "requested");

        const response = await postData(CNF_PREPARE_BILLING_REQUESTED_ENDPOINT, formData);
        if (response && (response?.code === 200 || response?.status === 200)) {
          alertPop("success", response.message || "Submitted successfully");
          setSelectedRowKeys([]);
          setSelectedRows([]);
          setCanRequest(false);
          refetch();
        }
        setLoading(false);
      },
    });
  };

  const handleWorkflowAction = async (status) => {
    if (!selectedRows.length) {
      notification.warning({
        message: "Warning",
        description: "No rows selected",
        placement: "topRight",
      });
      return;
    }

    const isResubmit = status === "requested";
    Modal.confirm({
      title: status === "approved"
        ? "Confirm approval"
        : isResubmit
          ? "Confirm re-submission"
          : "Confirm rejection",
      content: status === "approved"
        ? "Approve the selected prepare billing request(s)?"
        : isResubmit
          ? "Re-submit the selected prepare billing request(s) for approval?"
          : "Reject the selected prepare billing request(s)?",
      okText: "Yes",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append("requested_id", JSON.stringify(selectedRows.map((r) => r.id)));
        formData.append("status", status);

        const response = await postData(CNF_PREPARE_BILLING_REQUESTED_ENDPOINT, formData);
        if (response && (response?.code === 200 || response?.status === 200)) {
          alertPop("success", response.message || "Submitted successfully");
          setSelectedRowKeys([]);
          setSelectedRows([]);
          refetch();
        }
        setLoading(false);
      },
    });
  };

  const handleDisburse = async () => {
    try {
      const values = await disburseForm.validateFields();
      setLoading(true);
      const response = await postData(CNF_PREPARE_BILLING_DISBURSE_ENDPOINT, {
        prepare_billing_id: disburseRecord.id,
        receive_by: values.receive_by,
      });
      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message || "Disbursed successfully",
          placement: "topRight",
        });
        setDisburseModalVisible(false);
        setDisburseRecord(null);
        disburseForm.resetFields();
        refetch();
      }
    } catch (e) {
      // validation
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: "Bill number", dataIndex: "bill_number", key: "bill_number" },
      { title: "Items", dataIndex: "items_count", key: "items_count", width: 80 },
      {
        title: "Total (Taka)",
        dataIndex: "total_amount",
        key: "total_amount",
        align: "right",
        render: (v) => formatAmountTaka(v),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => <PrepareBillingStatusTag status={status} />,
      },
      { title: "Created", dataIndex: "created_at", key: "created_at" },
      {
        title: "Created by",
        key: "user",
        render: (_, r) => r.user?.name || "—",
      },
      {
        title: "Action",
        key: "action",
        width: 100,
        render: (_, record) => {
          const isCreator = loggedInUserId === record?.user?.id;
          const canView = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "view",
            record?.department_approve,
            record?.director_approve,
          );
          const canUpdate = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "update",
            record?.department_approve,
            record?.director_approve,
          );
          const canEditApprovedAmount = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "editApprovedAmount",
            record?.department_approve,
            record?.director_approve,
          );
          const canDisburse = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "disburse",
            record?.department_approve,
            record?.director_approve,
          );

          const menuItems = [
            canView &&
              hasPermission([EXTERNAL_CNF_BILLS_VIEW_PERMISSION]) && {
                key: "view",
                label: "View",
                icon: <EyeOutlined />,
                onClick: () => navigate(`${basePath}/view/${record.id}`),
              },
            (canUpdate || canEditApprovedAmount) &&
              hasPermission([EXTERNAL_CNF_BILLS_EDIT_PERMISSION]) && {
                key: "edit",
                label: "Edit",
                icon: <EditOutlined />,
                onClick: () => navigate(`${basePath}/edit/${record.id}`),
              },
            canDisburse && {
              key: "disburse",
              label: "Disburse",
              icon: <CheckOutlined />,
              onClick: () => {
                setDisburseRecord(record);
                setDisburseModalVisible(true);
              },
            },
          ].filter(Boolean);

          if (!menuItems.length) {
            return "—";
          }

          return (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [loggedInUserId, loggedInUserRole, navigate],
  );

  const handleTableChange = (pagination) => {
    if (pagination.current !== currentPage) setCurrentPage(pagination.current);
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setCurrentPage(1);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => {
      const isCreator = loggedInUserId === record?.user?.id;
      const canAct =
        checkPermission(
          loggedInUserRole,
          record.status,
          isCreator,
          "request",
          record?.department_approve,
          record?.director_approve,
        ) ||
        checkPermission(
          loggedInUserRole,
          record.status,
          isCreator,
          "approve",
          record?.department_approve,
          record?.director_approve,
        ) ||
        checkPermission(
          loggedInUserRole,
          record.status,
          isCreator,
          "reject",
          record?.department_approve,
          record?.director_approve,
        );
      return { disabled: !canAct };
    },
  };

  const selectedStatus = selectedRows[0]?.status;
  const isSendRequestAction =
    canRequest && (selectedStatus === "created" || selectedStatus === "rejected");

  const showWorkflowButtons =
    loggedInUserRole === ROLES.DEPARTMENT_HEAD ||
    loggedInUserRole === ROLES.DIRECTOR ||
    loggedInUserRole === ROLES.ACCOUNTS ||
    canRequest ||
    canApprove ||
    canReject;

  return (
    <AppContent
      header={
        <AppPageHeader
          title={<Title level={5}>Prepare billing</Title>}
          extra={
            <div style={{ display: "flex", alignItems: "center" }}>
              {showWorkflowButtons && selectedRowKeys.length > 0 && canReject && (
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleWorkflowAction("rejected")}
                  style={{ marginRight: 10 }}
                >
                  Reject
                </Button>
              )}
              {showWorkflowButtons && selectedRowKeys.length > 0 && isSendRequestAction && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleSendRequest}
                  style={{ marginRight: 10 }}
                >
                  {selectedStatus === "created" ? "Send Request" : "Re-submit"}
                </Button>
              )}
              {showWorkflowButtons && selectedRowKeys.length > 0 && canApprove && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleWorkflowAction("approved")}
                  style={{ marginRight: 10 }}
                >
                  Approve
                </Button>
              )}
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={() => navigate(`${basePath}/add`)}
                disabled={!hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])
                    ? "Add New"
                    : "No permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            </div>
          }
        />
      }
      content={
        <>
          <FilterComponent
            tableData={tableData}
            filterAPI={fetchList}
            showFilter={showFilter}
            setShowFilter={setShowFilter}
          />
          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              rowKey="id"
              loading={loading}
              error={fetchError}
              onRetry={() => fetchList(filterData)}
              columns={columns}
              dataSource={tableData}
              rowSelection={rowSelection}
              pagination={{
                current: currentPage,
                pageSize,
                total: metaData?.total || 0,
                showSizeChanger: true,
                showTotal: (t) => `Total ${t} items`,
              }}
              onChange={handleTableChange}
              bordered
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: hasPermission([EXTERNAL_CNF_BILLS_VIEW_PERMISSION])
                  ? "No prepare billing records yet."
                  : "No permission to view this list.",
              }}
            />
          </div>
          <Modal
            title="Disburse Prepare Billing"
            open={disburseModalVisible}
            onCancel={() => {
              setDisburseModalVisible(false);
              setDisburseRecord(null);
              disburseForm.resetFields();
            }}
            onOk={handleDisburse}
            okText="Confirm Disbursement"
          >
            <p>Bill number: {disburseRecord?.bill_number || "—"}</p>
            <p>Total: {formatAmountTaka(disburseRecord?.total_amount)}</p>
            <Form form={disburseForm} layout="vertical">
              <Form.Item
                name="receive_by"
                label="Received By"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Name of recipient" />
              </Form.Item>
            </Form>
          </Modal>
        </>
      }
    />
  );
}
